import React from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Box, Button, FormControlLabel, Grid, Switch } from "@mui/material";

import { useLazyGetBranchListQuery, useLazyGetBranchesByScopeQuery } from "store/services/branchApi";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import { BASE_CONSTANTS } from "constants/baseConstants";
import { useGetDdlRegionListQuery } from "store/services/LocationsApi/regionApi";
import { useLazyGetMultipleDistrictsByMultipleRegionIdQuery } from "store/services/LocationsApi/districtApi";
import { useLazyGetMultipleMunicipalityByMultipleDistrictIdQuery } from "store/services/LocationsApi/municipalityApi";
import { useLazyGetDDLUnassignedUserListQuery } from "store/services/branchAssignmentApi";

export const BranchAssignmentForm = ({
  branchAssignmentDetail = null,
  isBusy = false,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      user: null,
      branches: [],
      status: true,
      hasCustomAssignment: false,
      region: [],
      district: [],
      municipality: [],
      assignmentType: null,
    },
  });

  const _watchRegion = useWatch({ control, name: "region" });
  const _watchDistrict = useWatch({ control, name: "district" });
  const _watchMunicipality = useWatch({ control, name: "municipality" });
  const _watchHasCustomAssignment = useWatch({
    control,
    name: "hasCustomAssignment",
  });
  const assignmentType = watch("assignmentType");

  // Fetch branches within the currently chosen geographic scope and
  // auto-populate the branches field with all of them. The user can
  // then deselect individual branches if they want to narrow further.
  const [fetchScopeBranches, { data: scopeBranchRes }] =
    useLazyGetBranchesByScopeQuery();

  React.useEffect(() => {
    if (
      !assignmentType ||
      watch("hasCustomAssignment") ||
      assignmentType === "Custom"
    ) {
      return;
    }

    const regionIds =
      _watchRegion?.map((r) => r?.id).filter(Boolean).join(",") || "";
    const districtIds =
      _watchDistrict?.map((d) => d?.id).filter(Boolean).join(",") || "";
    const municipalityIds =
      _watchMunicipality?.map((m) => m?.id).filter(Boolean).join(",") || "";

    const hasScopeSelection =
      assignmentType === "Region"
        ? !!regionIds
        : assignmentType === "District"
        ? !!districtIds
        : !!municipalityIds;

    if (!hasScopeSelection) {
      setValue("branches", []);
      return;
    }

    fetchScopeBranches({
      assignmentType,
      regionIds,
      districtIds,
      municipalityIds,
    })
      .unwrap()
      .then((res) => {
        const all = (res?.data ?? []).map((b) => ({
          label: b?.name,
          id: b?._id,
        }));
        setValue("branches", all);
      })
      .catch(() => {
        setValue("branches", []);
      });
  }, [
    assignmentType,
    _watchRegion,
    _watchDistrict,
    _watchMunicipality,
    fetchScopeBranches,
    setValue,
    watch,
  ]);

  // RTK QUERY for fetching users
  const [
    fetchUsers,
    { data: userList = [], isLoading: loadingUsers, isFetching: fetchingUsers },
  ] = useLazyGetDDLUnassignedUserListQuery(); // get only un Assign Branch User

  const [
    fetchBranches,
    {
      data: branchList = [],
      isLoading: loadingBranches,
      isFetching: fetchingBranches,
    },
  ] = useLazyGetBranchListQuery();

  const {
    data: regionList = [],
    isLoading: loadingRegions,
    isFetching: fetchingRegions,
  } = useGetDdlRegionListQuery();

  const [
    fetchDistricts,
    { data: districtRes = [], isLoading: loadingDistrictss },
  ] = useLazyGetMultipleDistrictsByMultipleRegionIdQuery();

  const [
    fetchMunicipality,
    { data: municipalityRes = [], isLoading: loadingMunicipality },
  ] = useLazyGetMultipleMunicipalityByMultipleDistrictIdQuery();

  const _mappedUsers = React.useMemo(
    () => userList?.map((el) => ({ label: el?.name, id: el?._id })),
    [userList]
  );

  const assignmentTypeOptions = [
    { label: "Province", value: "Region" },
    { label: "District", value: "District" },
    { label: "Municipality", value: "Municipality" },
  ];
  React.useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const _mappedBranches = React.useMemo(
    () =>
      Array.isArray(branchList.data)
        ? branchList.data.map((el) => ({ label: el?.name, id: el?._id }))
        : [],
    [branchList.data]
  );

  const _mappedRegions = React.useMemo(
    () =>
      Array.isArray(regionList)
        ? regionList.map((el) => ({ label: el?.name, id: el?._id }))
        : [],
    [regionList]
  );

  const _mappedDistricts = React.useMemo(
    () =>
      Array.isArray(districtRes?.data)
        ? districtRes?.data?.map((el) => ({ label: el?.name, id: el?._id }))
        : [],
    [districtRes]
  );

  const _mappedMunicipalities = React.useMemo(
    () =>
      Array.isArray(municipalityRes?.data)
        ? municipalityRes?.data.map((el) => ({ label: el?.name, id: el?._id }))
        : [],
    [municipalityRes]
  );

  const _mappedScopeBranches = React.useMemo(
    () =>
      Array.isArray(scopeBranchRes?.data)
        ? scopeBranchRes.data.map((el) => ({
          label: el?.name,
          id: el?._id,
        }))
        : [],
    [scopeBranchRes],
  );

  React.useEffect(() => {
    if (_watchHasCustomAssignment) {
      fetchBranches({ status: "Active" });
    }
  }, [_watchHasCustomAssignment, fetchBranches]);

  React.useEffect(() => {
    if (_watchRegion?.length > 0) {
      if (assignmentType === "District" || assignmentType === "Municipality") {
        const regionIds = _watchRegion?.map((region) => region?.id).join(",");
        fetchDistricts({ regionIds });
      }
    } else {
      setValue("district", []);
      setValue("municipality", []);
    }
  }, [_watchRegion, assignmentType, fetchDistricts, setValue]);

  React.useEffect(() => {
    if (_watchDistrict?.length > 0 && assignmentType === "Municipality") {
      const districtIds = _watchDistrict
        .map((district) => district?.id)
        .join(",");
      fetchMunicipality({ districtId: districtIds });
    }
  }, [_watchDistrict, assignmentType, fetchMunicipality]);

  React.useEffect(() => {
    if (!branchAssignmentDetail) return;

    reset({
      user: branchAssignmentDetail?.user
        ? {
          label: branchAssignmentDetail?.user?.name,
          id: branchAssignmentDetail?.user?._id,
        }
        : null,
      branches:
        branchAssignmentDetail?.hasCustomAssignment
          ? branchAssignmentDetail?.customBranches?.map((item) => ({
            label: item?.name ?? "Unknown Branch",
            id: item?._id ?? "N/A",
          })) ?? []
          : branchAssignmentDetail?.assignedBranches?.map((item) => ({
            label: item?.name ?? "Unknown Branch",
            id: item?._id ?? "N/A",
          })) ?? [],
      status: branchAssignmentDetail?.status === "Inactive" ? false : true,
      hasCustomAssignment: !!branchAssignmentDetail?.hasCustomAssignment,
      assignmentType: branchAssignmentDetail?.assignmentType || "",

      ...(branchAssignmentDetail?.assignmentType === "Region" && {
        region:
          branchAssignmentDetail?.assignments?.map((region) => ({
            label: region?.name,
            id: region?._id,
          })) ?? [],
      }),

      ...(branchAssignmentDetail?.assignmentType === "District" &&
        (() => {
          const uniqueDistricts = Array.from(
            new Map(
              branchAssignmentDetail?.assignments?.map((district) => [
                district?._id,
                district,
              ])
            ).values()
          );

          const uniqueRegions = Array.from(
            new Map(
              uniqueDistricts
                .filter((district) => district?.region)
                .map((district) => [district?.region?._id, district?.region])
            ).values()
          ).map((region) => ({
            id: region._id,
            label: region.name,
          }));

          return {
            district: uniqueDistricts.map((district) => ({
              label: district?.name,
              id: district?._id,
            })),
            region: uniqueRegions,
          };
        })()),

      ...(branchAssignmentDetail?.assignmentType === "Municipality" &&
        (() => {
          const assignments = branchAssignmentDetail?.assignments ?? [];

          const uniqueDistricts = Array.from(
            new Map(
              assignments
                .filter((muncipality) => muncipality?.district)
                .map((muncipality) => [
                  muncipality?.district?._id,
                  muncipality?.district,
                ])
            ).values()
          ).map((district) => ({
            id: district._id,
            label: district.name,
          }));

          const uniqueRegions = Array.from(
            new Map(
              assignments
                .filter((muncipality) => muncipality?.district?.region)
                .map((muncipality) => [
                  muncipality?.district?.region?._id,
                  muncipality?.district?.region,
                ])
            ).values()
          ).map((region) => ({
            id: region._id,
            label: region.name,
          }));

          return {
            municipality: assignments.map((muncipality) => ({
              label: muncipality?.name ?? "No Municipality",
              id: muncipality?._id ?? "No ID",
              district: muncipality?.district?.name ?? "No District",
              region: muncipality?.district?.region?.name ?? "No Region",
            })),
            district: uniqueDistricts,
            region: uniqueRegions,
          };
        })()),
    });
  }, [branchAssignmentDetail, reset]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={BASE_CONSTANTS.SPACING_LG}>
          <Grid item xs={6}>
            <Controller
              name="user"
              control={control}
              rules={{ required: "Please Select User" }}
              render={({ field }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="User"
                  loading={loadingUsers || fetchingUsers}
                  loadingText="Loading Users..."
                  options={_mappedUsers}
                  value={field?.value ?? ""}
                  onChange={(_, data) => {
                    setValue("user", data);
                    field.onChange(data);
                  }}
                  error={!!errors?.user}
                  helperText={errors?.user?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="hasCustomAssignment"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        field.onChange(checked);
                        setValue("hasCustomAssignment", checked);
                        if (!checked) {
                          setValue("assignmentType", []);
                          setValue("region", []);
                          setValue("district", []);
                          setValue("municipality", []);
                          setValue("branches", []);
                        }
                        if (checked) {
                          // Switching to custom mode clears geographic selections
                          setValue("assignmentType", "");
                          setValue("region", []);
                          setValue("district", []);
                          setValue("municipality", []);
                        }
                      }}
                      color="secondary"
                    />
                  }
                  label="Custom Assignment"
                />
              )}
            />
          </Grid>

          {watch("hasCustomAssignment") === false && (
            <>
              <Grid item xs={6}>
                <Controller
                  name="assignmentType"
                  control={control}
                  rules={{ required: "Please Select Assignment Type" }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      fullWidth
                      required
                      label="Assignment Type"
                      loading={loadingUsers || fetchingUsers}
                      loadingText="Loading Assignment Type..."
                      options={assignmentTypeOptions}
                      value={
                        assignmentTypeOptions.find(
                          (option) => option.value === field.value
                        ) || null
                      }
                      onChange={(_, data) => {
                        setValue("assignmentType", data?.value ?? "");
                        setValue("region", []);
                        setValue("district", []);
                        setValue("municipality", []);
                        setValue("branches", []);
                      }}
                      error={!!errors?.assignmentType}
                      helperText={errors?.assignmentType?.message}
                    />
                  )}
                />
              </Grid>

              {assignmentType === "Region" && (
                <>
                  <Grid item xs={6}>
                    <Controller
                      name="region"
                      control={control}
                      rules={{ required: "Please Select a Province." }}
                      render={({ field }) => (
                        <BaseAutocomplete
                          fullWidth
                          required
                          multiple
                          label="Provinces"
                          loading={loadingRegions || fetchingRegions}
                          loadingText="Loading Provinces..."
                          options={_mappedRegions ?? []}
                          value={field?.value ?? []}
                          onChange={(_, data) => {
                            setValue("region", data || []);
                            field.onChange(data);
                          }}
                          error={!!errors?.region}
                          helperText={errors?.region?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="branches"
                      control={control}
                      render={({ field }) => (
                        <BaseAutocomplete
                          fullWidth
                          multiple
                          label="Branches"
                          loadingText="Loading Branches..."
                          options={_mappedScopeBranches ?? []}
                          value={field?.value ?? []}
                          onChange={(_, data) => {
                            setValue("branches", data || []);
                            field.onChange(data);
                          }}
                          helperText="All branches in the selected Provinces are auto-selected. Remove any to narrow down."
                        />
                      )}
                    />
                  </Grid>
                </>
              )}

              {assignmentType === "District" && (
                <>
                  <Grid item xs={6}>
                    <Controller
                      name="region"
                      control={control}
                      rules={{ required: "Please Select a Province." }}
                      render={({ field }) => (
                        <BaseAutocomplete
                          fullWidth
                          required
                          multiple
                          label="Provinces"
                          loading={loadingRegions || fetchingRegions}
                          loadingText="Loading Provinces..."
                          options={_mappedRegions ?? []}
                          value={field?.value ?? null}
                          onChange={(_, data) => {
                            setValue("region", data || []);
                            setValue("district", []);
                            field.onChange(data);
                          }}
                          error={!!errors?.region}
                          helperText={errors?.region?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item md={6}>
                    <Controller
                      name="district"
                      control={control}
                      rules={{ required: "Please Select a District." }}
                      render={({ field }) => (
                        <BaseAutocomplete
                          fullWidth
                          required
                          multiple
                          label="Districts"
                          loading={loadingDistrictss}
                          loadingText="Loading District..."
                          options={_mappedDistricts ?? []}
                          value={field?.value ?? []}
                          onChange={(_, data) => {
                            setValue("district", data || []);
                            setValue("municipality", []);
                            field.onChange(data);
                          }}
                          error={!!errors?.district}
                          helperText={errors?.district?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="branches"
                      control={control}
                      render={({ field }) => (
                        <BaseAutocomplete
                          fullWidth
                          multiple
                          label="Branches"
                          loadingText="Loading Branches..."
                          options={_mappedScopeBranches ?? []}
                          value={field?.value ?? []}
                          onChange={(_, data) => {
                            setValue("branches", data || []);
                            field.onChange(data);
                          }}
                          helperText="All branches in the selected Districts are auto-selected. Remove any to narrow down."
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
              {assignmentType === "Municipality" && (
                <>
                  <Grid item xs={6}>
                    <Controller
                      name="region"
                      control={control}
                      rules={{ required: "Please Select a Province." }}
                      render={({ field }) => (
                        <BaseAutocomplete
                          fullWidth
                          required
                          multiple
                          label="Provinces"
                          loading={loadingRegions || fetchingRegions}
                          loadingText="Loading Provinces..."
                          options={_mappedRegions ?? []}
                          value={field?.value ?? []}
                          onChange={(_, data) => {
                            setValue("region", data || []);
                            setValue("district", []);
                            setValue("municipality", []);
                            field.onChange(data);
                          }}
                          error={!!errors?.region}
                          helperText={errors?.region?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item md={6}>
                    <Controller
                      name="district"
                      control={control}
                      rules={{ required: "Please Select a District." }}
                      render={({ field }) => (
                        <BaseAutocomplete
                          fullWidth
                          required
                          multiple
                          label="Districts"
                          loading={loadingDistrictss}
                          loadingText="Loading District..."
                          options={_mappedDistricts ?? []}
                          value={field?.value ?? []}
                          onChange={(_, data) => {
                            setValue("district", data || []);
                            setValue("municipality", []);
                            field.onChange(data);
                          }}
                          error={!!errors?.district}
                          helperText={errors?.district?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item md={6}>
                    <Controller
                      name="municipality"
                      control={control}
                      rules={{ required: "Please Select a Municipality." }}
                      render={({ field }) => (
                        <BaseAutocomplete
                          fullWidth
                          required
                          multiple
                          label="Municipalities"
                          loading={loadingMunicipality}
                          loadingText="Loading Municipality..."
                          options={_mappedMunicipalities ?? []}
                          value={field?.value ?? []}
                          onChange={(_, data) => {
                            setValue("municipality", data || []);
                          }}
                          error={!!errors?.municipality}
                          helperText={errors?.municipality?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="branches"
                      control={control}
                      render={({ field }) => (
                        <BaseAutocomplete
                          fullWidth
                          multiple
                          label="Branches"
                          loadingText="Loading Branches..."
                          options={_mappedScopeBranches ?? []}
                          value={field?.value ?? []}
                          onChange={(_, data) => {
                            setValue("branches", data || []);
                            field.onChange(data);
                          }}
                          helperText="All branches in the selected Municipalities are auto-selected. Remove any to narrow down."
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
            </>
          )}

          {watch("hasCustomAssignment") === true && (
            <Grid item xs={6}>
              <Controller
                name="branches"
                control={control}
                rules={{ required: "Please Select a Branch." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    multiple
                    label="Branch"
                    loading={loadingBranches || fetchingBranches}
                    loadingText="Loading Branches..."
                    options={_mappedBranches}
                    value={field?.value ?? ""}
                    onChange={(_, data) => {
                      setValue("branches", data);
                      field.onChange(data);
                    }}
                    error={!!errors?.branches}
                    helperText={errors?.branches?.message}
                  />
                )}
              />
            </Grid>
          )}

          {branchAssignmentDetail && (
            <Grid item xs={6}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>

        <Box
          paddingY={2}
          gap={2}
          style={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button disabled={isBusy} onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isBusy}
            onClick={handleSubmit((data) => onSubmit(data))}
          >
            {branchAssignmentDetail ? "Update" : "Add"}
          </Button>
        </Box>
      </AppForm.Content>
    </AppForm>
  );
};

export default BranchAssignmentForm;
